import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import { debounce } from 'lodash'
import {
  FunctionalUnitDTO,
  FunctionalUnitService,
} from '@services/functionalUnitService'
import { HospitalUnitType } from '@services/dtos'

interface FunctionalUnitSelectProps {
  value?: FunctionalUnitDTO | null
  onChange: (functionalUnit: FunctionalUnitDTO | null) => void
  label?: string
  placeholder?: string
  error?: string
  size?: 'small' | 'medium'
  disabled?: boolean
  required?: boolean
  functionType?: HospitalUnitType
}

export const FunctionalUnitSelect: React.FC<FunctionalUnitSelectProps> = ({
  value = null,
  onChange,
  label = 'Unité fonctionnelle',
  placeholder = 'Sélectionner une unité fonctionnelle',
  error,
  size = 'medium',
  disabled = false,
  required = false,
  functionType,
}) => {
  const [functionalUnits, setFunctionalUnits] = useState<FunctionalUnitDTO[]>(
    [],
  )
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState(value?.name || '')
  const latestRequestId = useRef(0)
  const isMountedRef = useRef(true)

  // Filter functional units locally by type if provided
  const filteredFunctionalUnits = useMemo(() => {
    if (!functionType) return functionalUnits
    return functionalUnits.filter((unit) => unit.functionType === functionType)
  }, [functionalUnits, functionType])

  const functionalUnitOptions = useMemo(() => {
    if (
      value &&
      !filteredFunctionalUnits.some((unit) => unit.id === value.id)
    ) {
      return [value, ...filteredFunctionalUnits]
    }

    return filteredFunctionalUnits
  }, [filteredFunctionalUnits, value])

  const fetchFunctionalUnits = useCallback(
    async (searchTerm = '') => {
      const requestId = latestRequestId.current + 1
      latestRequestId.current = requestId
      setLoading(true)

      try {
        const result = await FunctionalUnitService.getAll({
          ...(searchTerm ? { search: searchTerm } : {}),
          functionType,
        })

        if (isMountedRef.current && latestRequestId.current === requestId) {
          setFunctionalUnits(result.datas || [])
        }
      } catch (error) {
        console.error('Error fetching functional units:', error)
      } finally {
        if (isMountedRef.current && latestRequestId.current === requestId) {
          setLoading(false)
        }
      }
    },
    [functionType],
  )

  const debouncedFetchAll = useMemo(
    () =>
      debounce((searchTerm: string) => {
        fetchFunctionalUnits(searchTerm)
      }, 300),
    [fetchFunctionalUnits],
  )

  useEffect(() => {
    fetchFunctionalUnits()
  }, [fetchFunctionalUnits])

  useEffect(() => {
    setInputValue(value?.name || '')
  }, [value?.id, value?.name])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    return () => {
      debouncedFetchAll.cancel()
    }
  }, [debouncedFetchAll])

  const handleInputChange = (
    _: any,
    newInputValue: string,
    reason: string,
  ) => {
    setInputValue(newInputValue)

    if (reason === 'reset') {
      return
    }

    debouncedFetchAll.cancel()

    const searchTerm = newInputValue.trim()
    if (searchTerm) {
      debouncedFetchAll(searchTerm)
    } else {
      fetchFunctionalUnits()
    }
  }

  const handleChange = (_: any, newValue: FunctionalUnitDTO | null) => {
    onChange(newValue)
  }

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={functionalUnitOptions}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      disabled={disabled}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error}
          required={required}
          InputProps={{
            ...params.InputProps,
            inputProps: {
              ...params.inputProps,
              autoComplete: 'new-password',
              'data-form-type': 'other', // Additional hint for some browsers
            },
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <div>
            <div style={{ fontWeight: 500 }}>{option.name}</div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              {option.site?.label} - {option.careType}
            </div>
          </div>
        </li>
      )}
    />
  )
}

export default FunctionalUnitSelect
